import "./KeyboardShortcutsHelp.scss";

import ButtonIcon from "components/ButtonIcon/ButtonIcon";
import Card from "components/Card/Card";
import KaboodleModal from "components/Modals/KaboodleModal";
import { useState } from "react";

type KeyboardShortcut = {
  keys: string[];
  description: string;
  category: "navigation" | "selection";
};

const shortcuts: KeyboardShortcut[] = [
  // navigation
  {
    keys: ["↑"],
    description: "Navigate up one page",
    category: "navigation",
  },
  {
    keys: ["↓"],
    description: "Navigate down one page",
    category: "navigation",
  },
  {
    keys: ["Shift", "↑"],
    description: "Jump 10 pages up and select range",
    category: "navigation",
  },
  {
    keys: ["Shift", "↓"],
    description: "Jump 10 pages down and select range",
    category: "navigation",
  },
  {
    keys: ["←"],
    description: "Jump to first page",
    category: "navigation",
  },
  {
    keys: ["→"],
    description: "Jump to last page",
    category: "navigation",
  },
  {
    keys: ["Ctrl", "["],
    description: "Jump to previous untagged page (Ops view)",
    category: "navigation",
  },
  {
    keys: ["Ctrl", "]"],
    description: "Jump to next untagged page (Ops view)",
    category: "navigation",
  },
  {
    keys: ["Cmd/Ctrl", "K"],
    description: "Focus page input",
    category: "navigation",
  },
  {
    keys: ["Ctrl", "F"],
    description: "Open search",
    category: "navigation",
  },
  {
    keys: ["Ctrl", "+"],
    description: "Zoom in",
    category: "navigation",
  },
  {
    keys: ["Ctrl", "-"],
    description: "Zoom out",
    category: "navigation",
  },
  {
    keys: ["Ctrl", "R"],
    description: "Reset zoom",
    category: "navigation",
  },
  // selection
  {
    keys: ["Space"],
    description: "Toggle checkbox",
    category: "selection",
  },
  {
    keys: ["Esc"],
    description: "Clear selection",
    category: "selection",
  },
  {
    keys: ["D"],
    description: "Delete selected pages",
    category: "selection",
  },
  {
    keys: ["Shift", "Type key (A-Z / 0-9)"],
    description:
      "Open range assign modal for a document type (for example: Shift + 1)",
    category: "selection",
  },
];

type KeyboardShortcutsHelpProps = {
  iconOnly?: boolean;
};

const KeyboardShortcutsHelp = ({
  iconOnly = false,
}: KeyboardShortcutsHelpProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);

  const renderKey = (key: string) => {
    // special icons for certain keys with optional labels
    const keyConfig: Record<string, { icon?: string; label?: string }> = {
      "↑": { icon: "arrow_upward" },
      "↓": { icon: "arrow_downward" },
      "←": { icon: "arrow_back" },
      "→": { icon: "arrow_forward" },
      "⌘": { icon: "keyboard_command_key" },
      "Cmd/Ctrl": { icon: "keyboard_command_key" },
      Enter: { icon: "keyboard_return" },
      Esc: { icon: "close" },
    };

    const config = keyConfig[key];

    if (config?.icon) {
      return (
        <kbd className="keyboard-shortcuts-help__key keyboard-shortcuts-help__key--icon">
          <span className="material-icons">{config.icon}</span>
          {config.label && (
            <span className="keyboard-shortcuts-help__key-label">
              {config.label}
            </span>
          )}
        </kbd>
      );
    }

    return <kbd className="keyboard-shortcuts-help__key">{key}</kbd>;
  };

  const categoryLabels = {
    navigation: "Navigation",
    selection: "Thumbnail Selection",
  };

  const groupedShortcuts = {
    navigation: shortcuts.filter((s) => s.category === "navigation"),
    selection: shortcuts.filter((s) => s.category === "selection"),
  };

  return (
    <>
      {iconOnly ? (
        <ButtonIcon onClick={handleOpen} title="Keyboard shortcuts">
          keyboard
        </ButtonIcon>
      ) : (
        <button
          className="keyboard-shortcuts-help__trigger"
          onClick={handleOpen}
          title="Keyboard shortcuts"
          aria-label="Show keyboard shortcuts"
        >
          <span className="material-icons">keyboard</span>
        </button>
      )}

      <KaboodleModal isOpen={isOpen} onClose={handleClose} size="large">
        <Card heading="Keyboard Shortcuts">
          <div className="keyboard-shortcuts-help__content">
            <div className="keyboard-shortcuts-help__tip-header">
              <div className="keyboard-shortcuts-help__tip">
                <span className="material-icons">lightbulb</span>
                <div className="keyboard-shortcuts-help__tip-content">
                  <strong>Quick workflow:</strong>
                  <ol className="keyboard-shortcuts-help__tip-steps">
                    <li>
                      Navigate thumbnails using {renderKey("↑")}{" "}
                      {renderKey("↓")} , or jump to the first/last page with{" "}
                      {renderKey("←")} {renderKey("→")}
                    </li>
                    <li>
                      Press {renderKey("Space")} to toggle the checkbox on the
                      current page
                    </li>
                    <li>
                      With a selection, use
                      {renderKey("↑")} {renderKey("↓")} to select a range. Press{" "}
                      {renderKey("Esc")} to clear your selection.
                    </li>
                    <li>
                      Once you have selected one or more pages, click a Document
                      Type or press the corresponding shortcut key to assign it
                    </li>
                    <li>
                      Press {renderKey("Shift")} + a type shortcut to assign a
                      document type to a page range (works with letters and
                      numbers, for example Shift + 1)
                    </li>
                  </ol>
                </div>
              </div>
            </div>

            {(
              Object.entries(groupedShortcuts) as [
                keyof typeof groupedShortcuts,
                KeyboardShortcut[],
              ][]
            ).map(([category, categoryShortcuts]) => (
              <div key={category} className="keyboard-shortcuts-help__section">
                <h3 className="keyboard-shortcuts-help__section-title">
                  {categoryLabels[category]}
                </h3>
                <div className="keyboard-shortcuts-help__shortcuts">
                  {categoryShortcuts.map((shortcut, index) => (
                    <div
                      key={`${category}-${index}`}
                      className="keyboard-shortcuts-help__shortcut"
                    >
                      <div className="keyboard-shortcuts-help__keys">
                        {shortcut.keys.map((key, keyIndex) => (
                          <span key={keyIndex}>
                            {renderKey(key)}
                            {keyIndex < shortcut.keys.length - 1 && (
                              <span className="keyboard-shortcuts-help__plus">
                                +
                              </span>
                            )}
                          </span>
                        ))}
                      </div>
                      <div className="keyboard-shortcuts-help__description">
                        {shortcut.description}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </KaboodleModal>
    </>
  );
};

export default KeyboardShortcutsHelp;
